import { EnvironmentProviders, inject, Injectable, Provider } from '@angular/core';
import {
  IFloatingPopupActions,
  IFloatingPopupConfig,
  IFloatingPopupGroupInstance,
} from '@app/main/main-content/floating-popup-manager/interfaces/floating-popup-instance';
import { FloatingPopupService } from '@app/main/main-content/floating-popup-manager/services/floating-popup.service';
import { IFloatingPopupEntityService } from '@app/main/main-content/floating-popup-manager/interfaces/floating-popup-entity-service';
import { FpLoggerNoiseContentComponent } from '@app/main/leak-detection/floating-popups/floating-popup-logger-noise/components/content/fp-logger-noise-content.component';
import { FpLoggerNoiseMinimizedContentComponent } from '@app/main/leak-detection/floating-popups/floating-popup-logger-noise/components/minimized-content/fp-logger-noise-minimized-content.component';
import { FpLoggerNoiseTabs } from '@app/main/leak-detection/floating-popups/floating-popup-logger-noise/enums/fp-logger-noise-tabs.enum';
import { FpLoggerNoiseTabsLocalize } from '@app/main/leak-detection/floating-popups/floating-popup-logger-noise/localize/fp-logger-noise-tabs';
import { _, TranslateService } from '@ngx-translate/core';
import { fpLoggerNoiseProvider } from '@app/main/leak-detection/floating-popups/floating-popup-logger-noise/providers/fp-logger-noise.providers';
import { ILdLogger } from '@app/main/leak-detection/interfaces/ld-logger';
import { combineLatest, of } from 'rxjs';
import { take } from 'rxjs/operators';
import { formatSerialNo } from '@app/shared/utils/string';
import { ITranslationSubscription } from '@app/core/interfaces/localization';
import { Store } from '@ngrx/store';
import { NgxPermissionsService } from 'ngx-permissions';
import { PermissionName } from '@app/core/permissions/enums/permission-name.enum';
import { selectMainProjectId } from '@app/main/main-screen/store/main-selection/main-shared-selection/main-shared-selection.selectors';
import { selectProjectEntities } from '@app/main/main-screen/store/projects/projects.selectors';
import { LeakDetectionMode } from '@app/core/enums/leak-detection-mode.enum';

@Injectable({ providedIn: 'root' })
export class FpLoggerNoiseService implements IFloatingPopupEntityService<ILdLogger> {
  protected popupService = inject(FloatingPopupService);
  private translateService = inject(TranslateService);
  private store = inject(Store);
  private ngxPermissionsService = inject(NgxPermissionsService);

  private readonly instanceProviders: (Provider | EnvironmentProviders)[] = [fpLoggerNoiseProvider()];

  /** Developer sees both prediction tabs; every other role sees only the one the project is set to. */
  private buildTabValues(leakDetectionMode: LeakDetectionMode): FpLoggerNoiseTabs[] {
    const isDeveloper = !!this.ngxPermissionsService.getPermission(PermissionName.LeakDetection_Read_LoggersGrouped);

    const predictionTabs = isDeveloper
      ? [FpLoggerNoiseTabs.LeakScore, FpLoggerNoiseTabs.AIPredictor]
      : [
          leakDetectionMode === LeakDetectionMode.LeakScore
            ? FpLoggerNoiseTabs.LeakScore
            : FpLoggerNoiseTabs.AIPredictor,
        ];

    return [
      FpLoggerNoiseTabs.Histogram,
      FpLoggerNoiseTabs.SignalSpectrum,
      ...predictionTabs,
      FpLoggerNoiseTabs.MinNoise,
      FpLoggerNoiseTabs.Spread,
      FpLoggerNoiseTabs.Summary,
      FpLoggerNoiseTabs.Comments,
      FpLoggerNoiseTabs.Pictures,
    ];
  }

  private buildFloatingPopupGroup(leakDetectionMode: LeakDetectionMode): IFloatingPopupGroupInstance {
    return {
      id: 'logger-noise-popups',
      orderWeight: 2,
      minimizeTitle: (count: number) =>
        this.translateService.instant(_('@@floating-popup-group-logger-noise-name'), { I18N_VAR_COUNT: count }),
      tabs: {
        values: this.buildTabValues(leakDetectionMode),
        localize: FpLoggerNoiseTabsLocalize,
      },
    };
  }

  public open(
    item: ILdLogger,
    actions?: IFloatingPopupActions,
    selectedTab: FpLoggerNoiseTabs = FpLoggerNoiseTabs.Histogram
  ) {
    combineLatest([this.store.select(selectMainProjectId), this.store.select(selectProjectEntities)])
      .pipe(take(1))
      .subscribe(([projectId, projectsMap]) => {
        const leakDetectionMode = projectsMap[projectId!]?.leakDetectionMode ?? LeakDetectionMode.LeakScore;
        const loggerSerialNo = item.deployment.serialNo;

        const title = this.translateService.get(_('@@floating-popup-header-logger-noise-name'), {
          I18N_VAR_LOGGER_SERIAL_NO: formatSerialNo(loggerSerialNo),
        });

        const shortTitle: ITranslationSubscription = of(`${formatSerialNo(loggerSerialNo)}`);

        const floatingPopupConfig: IFloatingPopupConfig = {
          component: FpLoggerNoiseContentComponent,
          minimizedComponent: FpLoggerNoiseMinimizedContentComponent,
          selectedTab,
          instanceProviders: this.instanceProviders,
          width: 840,
          height: 500,
          minWidth: 480,
          actions,
        };

        this.popupService.open<ILdLogger>(
          { ...floatingPopupConfig, title, shortTitle },
          item,
          this.buildFloatingPopupGroup(leakDetectionMode)
        );
      });
  }
}
